import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { PageHeader, Pager } from '@/components/kit';
import { ListBody, Stars } from '@/components/inputs';
import ReviewDialog from '@/components/account/ReviewDialog';
import { REVIEW_FIELDS } from '@/shared/fields';
import { planName } from '@/shared/pricing';
import { paged } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/account/reviews', layout: 'owner', access: "profile === 'Customer_c'" };
export const nav = { icon: 'Star', label: 'Reviews', section: 'My account', profiles: ['Customer_c'], order: 40 };

const PAGE_SIZE = 9;

export default function Reviews() {
  const [params, setParams] = useSearchParams();
  const orderParam = params.get('order');
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (orderParam) setOpen(true);
  }, [orderParam]);

  const { data, loading, error, run } = useFetch(
    () => sdk.table('reviews_c').select(REVIEW_FIELDS).orderByDesc('CreatedOn').page(page, PAGE_SIZE).fetch().then(paged),
    [page],
  );
  const list = data?.rows ?? [];

  function onOpenChange(next) {
    setOpen(next);
    if (!next && orderParam) setParams({}, { replace: true });
  }

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        eyebrow="My account"
        title="Reviews"
        description="What you've told other viewers. Edit or remove a review any time."
        actions={
          <Button onClick={() => setOpen(true)}>
            <ApperIcon name="Plus" size={16} />
            Write a review
          </Button>
        }
      />

      <ListBody
        loading={loading}
        data={data}
        error={error}
        run={run}
        isEmpty={!list.length}
        empty={{
          icon: 'Star',
          title: 'No reviews yet',
          description: 'Enjoying your line? A short review helps other viewers decide.',
          action: () => setOpen(true),
          actionLabel: 'Write a review',
        }}
      >
        <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {list.map((r) => (
            <li key={r.Id}>
              <Link
                to={`/account/reviews/${r.Id}`}
                className="flex h-full flex-col rounded-lg border border-border bg-card p-4 shadow-xs transition-colors hover:border-primary/50"
              >
                <div className="flex items-center justify-between gap-2">
                  <Stars value={r.rating_c} />
                  {r.hidden_c && (
                    <span className="rounded-sm border border-border bg-muted px-1.5 py-0.5 text-[11px] text-muted-foreground">
                      Hidden
                    </span>
                  )}
                </div>
                <p className="mt-3 font-medium">{r.Name}</p>
                <p className="mt-1 line-clamp-3 flex-1 text-sm text-muted-foreground">{r.body_c}</p>
                <p className="mt-3 text-xs text-muted-foreground">
                  {planName(r.plan_c)} · {r.order_c?.Name ?? 'Order'} · {formatLocalDate(r.CreatedOn, DATE_FORMATS.SHORT)}
                </p>
                {r.reply_c && (
                  <p className="mt-2 flex items-center gap-1.5 text-xs text-primary">
                    <ApperIcon name="MessageSquareReply" size={14} />
                    Ghost Cable replied
                  </p>
                )}
              </Link>
            </li>
          ))}
        </ul>
        <Pager page={page} total={data?.total} pageSize={PAGE_SIZE} hasMore={data?.hasMore} onPage={setPage} />
      </ListBody>

      <ReviewDialog open={open} onOpenChange={onOpenChange} defaultOrder={orderParam} onSaved={run} />
    </div>
  );
}
